import { Injectable } from '@angular/core';
import { AppConstants } from '../constants/constants';
import { User } from '../user/user.types';
import { RESTRICTED_USER_CAPABILITIES } from './restricted-users.config';

/**
 * Single source of truth for "can the current user see/do this action" checks
 * that aren't (yet) backed by a real backend permission. Components should
 * call `can()` - or use the `can` pipe in templates - instead of comparing
 * user identity directly.
 */
@Injectable({
  providedIn: 'root'
})
export class AccessControlService {

  /**
   * True unless the current user is on the restricted list and the
   * capability isn't in their explicitly allowed set.
   */
  can(capability: string): boolean {
    const email = this.getCurrentUserEmail();
    const allowedCapabilities = email ? RESTRICTED_USER_CAPABILITIES[email] : undefined;
    return !allowedCapabilities || allowedCapabilities.includes(capability);
  }

  /**
   * Reads the persisted user directly from localStorage rather than the
   * in-memory UserState BehaviorSubject. UserState is only populated by
   * InitialDataResolver, and Angular runs all canActivate guards for a route
   * tree BEFORE running any resolver in that same tree - so on a hard
   * reload/direct URL hit, a guard calling can() would see UserState still
   * null and default to "allowed". Reading localStorage directly makes this
   * synchronous and race-free for guards, while staying correct for
   * component-level checks too (UserState.setUserState always writes here
   * at the same time it updates the subject).
   */
  private getCurrentUserEmail(): string | undefined {
    try {
      const currentUser: User = JSON.parse(localStorage.getItem(AppConstants.PRISISTED_KEYS.CURRENT_USER));
      return currentUser?.email?.toLowerCase();
    } catch {
      return undefined;
    }
  }
}
